'use client'

import { useState, useEffect, useRef } from 'react'
import { Label, Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/16/solid'
import { CheckIcon } from '@heroicons/react/20/solid'
interface SelectProps {
  label? : string
  hideLabel?: boolean
  children?: React.ReactNode
  required?: boolean
  defaultValue?: string | number
  selectOptions: { value: string | number, name: string }[]
  onChange?: (value: string) => void
  error?: {
    message?: string // Allow message to be undefined
  }
}

const Select: React.FC<SelectProps> = ({label, selectOptions, error, required = false, hideLabel = false, defaultValue, onChange}) => {
  const [selected, setSelected] = useState(selectOptions[0])
  const [showBelow, setShowBelow] = useState(true) // State to determine dropdown position
  const [dropdownWidth, setDropdownWidth] = useState(298) // State to store dropdown width
  const buttonRef = useRef<HTMLDivElement>(null) // Ref for the button element
  const setValue = (value: { value: string, name: string }) => {
    setSelected(value)
    if (onChange) {
      onChange(value.value)
    }
  }

  useEffect(() => {
    if (defaultValue !== undefined && defaultValue !== '') {
      const defaultOption = selectOptions.find(option => 
        typeof option.value === 'string' && typeof defaultValue === 'string' 
          ? option.value.toLowerCase() === defaultValue.toLowerCase()
          : option.value === defaultValue
      )
      if (defaultOption) {
        setSelected(defaultOption)
      }
    }
  }, [defaultValue, selectOptions])

  useEffect(() => {
    const handlePosition = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect()
        const viewportHeight = window.innerHeight
        const spaceBelow = viewportHeight - rect.bottom
        const dropdownHeight = 240 // Approximate height of the dropdown (adjust as needed)

        // Set `showBelow` to true if there's enough space below, otherwise false
        setShowBelow(spaceBelow >= dropdownHeight)

        // Dynamically set the dropdown width based on the button width
        setDropdownWidth(rect.width)
      }
    }

    handlePosition()
    window.addEventListener('resize', handlePosition)
    return () => {
      window.removeEventListener('resize', handlePosition)
    }
  }, [])

  return (
    <div>
      <Listbox value={selected} onChange={setValue}>
        <Label className={`${error ? 'text-[var(--theme-danger)]' : 'text-[var(--theme-text)]'} block pl-2.5 text-sm/6 font-[var(--font-weight-bold)] h-6 leading-4 text-pretty ${required && !hideLabel ? 'after:ml-1.5 after:content-["*"]' : ''}`}>
          { !hideLabel ?
            <>{label}</>
            :
            <>
              &nbsp;
              <span className='sr-only'>{label}</span>
            </>
          }
          </Label>
        <div className='relative mt-2.5' ref={buttonRef}>
          <ListboxButton
            data-error={error ? "true" : "false"}
            className={`
              grid w-full cursor-pointer grid-cols-1 rounded-md bg-white py-[11px] pr-2 pl-3 text-left
              outline outline-1 -outline-offset-1 outline-[var(--input-border)] focus:outline-1 focus:-outline-offset-1
              ${error ? 'outline-[var(--theme-danger)] focus:outline-[var(--theme-danger)]' : 'focus:outline-[var(--theme-text)]'}
              focus:outline-[var(--theme-text)] text-lg ${selected.value === '' ? 'text-[var(--input-placeholder)]' : 'text-[var(--input-text)]'}
            `}>
            <span className='col-start-1 row-start-1 truncate pr-6'>{selected.name}</span>
            <ChevronDownIcon
              aria-hidden='true'
              className='col-start-1 row-start-1 size-5 self-center justify-self-end'
            />
          </ListboxButton>

          <ListboxOptions
            anchor={showBelow? 'top' : 'bottom'}
            modal={false}
            transition
            style={{ width: `${dropdownWidth}px`}} // Dynamically set the width
            className='
            absolute z-50 mt-1 max-h-60 overflow-auto rounded-md bg-white outline outline-1 outline-[var(--const-gray-light)] py-1 text-base shadow-lg data-leave:transition data-leave:duration-100 data-leave:ease-in data-closed:data-leave:opacity-0 sm:text-sm'
          >
            {selectOptions.map((person) => (
              <ListboxOption
                key={person.value}
                value={person}
                className='
                  group relative cursor-pointer py-2.5 pr-9 pl-3 text-lg text-[var(--input-text)] select-none transition duration-500
                  data-[focus]:bg-[var(--brand-1)] data-[focus]:text-white data-[focus]:outline-hidden border-b border-[var(--dropdown-divider)] last:border-none data-[selected]:bg-[var(--brand-1)] data-[selected]:cursor-default data-[selected]:text-white first:hidden
                '
              >
                <span className='block truncate'>{person.name}</span>

                <span className='absolute inset-y-0 right-0 hidden items-center pr-4 text-[var(--const-white)] group-data-[selected]:flex group-data-[focus]:text-white'>
                  <CheckIcon aria-hidden='true' className='size-5' />
                </span>
              </ListboxOption>
            ))}
            </ListboxOptions>
        </div>
      </Listbox>
      {error && error.message && <p className='p-0.5 text-md text-[var(--theme-danger)]'>{error.message}</p>}
    </div>
  )
}

export {Select}